import { useState } from 'react';
import { Avatar, Badge, Button } from '@klaro7/ui';
import type { PaginationMeta } from '@klaro7/shared-types';

import type { Lead } from '../services/leads.service';
import { useGenerateQuestionnaire, useSendWelcomeMail } from '../hooks/useLeads';

interface LeadTableProps {
  leads: Lead[];
  pagination?: PaginationMeta;
  page: number;
  onPageChange: (page: number) => void;
}

const questionnaireStatusLabels: Record<string, { label: string; variant: 'warning' | 'info' | 'success' | 'error' }> = {
  in_progress: { label: 'In corso', variant: 'warning' },
  completed: { label: 'Completato', variant: 'info' },
  approved: { label: 'Approvato', variant: 'success' },
  rejected: { label: 'Rifiutato', variant: 'error' },
};

function CopyLinkButton({ url }: { url: string }) {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Clipboard API may fail in insecure contexts
    }
  };

  if (copied) {
    return (
      <span className="inline-flex items-center gap-1 text-xs font-medium text-green-600">
        <svg className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
        </svg>
        Copiato!
      </span>
    );
  }

  return (
    <button
      onClick={handleCopy}
      title={url}
      className="inline-flex items-center gap-1 rounded border border-neutral-200 bg-white px-2 py-0.5 text-[11px] font-medium text-neutral-600 shadow-sm hover:bg-neutral-50 hover:text-neutral-700 transition-colors"
    >
      <svg className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
      </svg>
      Copia
    </button>
  );
}

function GenerateButton({ leadId }: { leadId: string }) {
  const generateMutation = useGenerateQuestionnaire();

  return (
    <Button
      variant="outline"
      size="sm"
      onClick={() => generateMutation.mutate(leadId)}
      loading={generateMutation.isPending}
    >
      Genera questionario
    </Button>
  );
}

function ResendQuestionnaireMailButton({ leadId }: { leadId: string }) {
  const sendMutation = useSendWelcomeMail();

  return (
    <Button
      variant="outline"
      size="sm"
      onClick={() => sendMutation.mutate(leadId)}
      loading={sendMutation.isPending}
      title="Re-invia mail del questionario"
    >
      Re-invia mail
    </Button>
  );
}

type MailDisplayStatus = 'sent' | 'failed' | 'pending';

function getMailDisplayStatus(lead: Lead): MailDisplayStatus {
  if (lead.welcomeMailStatus === 'failed') return 'failed';
  if (lead.welcomeMailStatus === 'sent' || lead.welcomeMailSent) return 'sent';
  return 'pending';
}

function formatMailDate(value?: string | null): string | null {
  if (!value) return null;
  return new Intl.DateTimeFormat('it-IT', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(new Date(value));
}

function buildMailTooltip(lead: Lead, status: MailDisplayStatus): string {
  const lastAttemptAt = formatMailDate(lead.welcomeMailLastAttemptAt ?? lead.welcomeMailSentAt);
  const sentAt = formatMailDate(lead.welcomeMailSentAt);

  if (status === 'sent') {
    return lastAttemptAt
      ? `Inviata (SMTP confermato) · ${lastAttemptAt}`
      : 'Inviata (SMTP confermato)';
  }

  if (status === 'failed') {
    const lines = [
      lastAttemptAt ? `Invio fallito · ${lastAttemptAt}` : 'Invio fallito',
      lead.welcomeMailLastError || 'Dettaglio errore non disponibile',
    ];
    if (sentAt) lines.push(`Ultimo OK · ${sentAt}`);
    return lines.join('\n');
  }

  return 'Mail non ancora inviata';
}

// Shared mail status icon: green double-check (SMTP-confirmed sent),
// red X (failed), gray clock (pending). Detail in the hover tooltip.
function MailStatusIcon({ lead }: { lead: Lead }) {
  const status = getMailDisplayStatus(lead);
  const tooltip = buildMailTooltip(lead, status);
  const ariaLabel =
    status === 'sent' ? 'Inviata' : status === 'failed' ? 'Invio fallito' : 'Mail non ancora inviata';

  return (
    <span
      title={tooltip}
      aria-label={ariaLabel}
      className={
        status === 'sent'
          ? 'inline-flex text-green-600'
          : status === 'failed'
            ? 'inline-flex text-red-600'
            : 'inline-flex text-neutral-400'
      }
    >
      {status === 'sent' ? (
        // Double-check (SMTP confirmed)
        <svg className="h-4 w-4" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
          <path d="M18 7l-1.41-1.41-6.34 6.34 1.41 1.41L18 7zm4.24-1.41L11.66 16.17 7.48 12l-1.41 1.41L11.66 19l12-12-1.42-1.41zM.41 13.41L6 19l1.41-1.41L1.83 12 .41 13.41z" />
        </svg>
      ) : status === 'failed' ? (
        <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5} aria-hidden="true">
          <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
        </svg>
      ) : (
        <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} aria-hidden="true">
          <path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      )}
    </span>
  );
}

function WelcomeMailCell({ lead }: { lead: Lead }) {
  const sendMutation = useSendWelcomeMail();
  const status = getMailDisplayStatus(lead);
  const buttonLabel = status === 'pending' ? 'Invia' : 'Re-invia';
  const mailKind = lead.type === 'PROFESSIONAL' ? 'mail del questionario' : 'welcome mail';

  return (
    <div className="flex flex-wrap items-center gap-2">
      <MailStatusIcon lead={lead} />
      <Button
        variant="outline"
        size="sm"
        onClick={() => sendMutation.mutate(lead._id)}
        loading={sendMutation.isPending}
        title={`${buttonLabel} ${mailKind}`}
      >
        {buttonLabel}
      </Button>
    </div>
  );
}

export function LeadTable({ leads, pagination, page, onPageChange }: LeadTableProps) {
  if (leads.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-center">
        <div className="flex h-14 w-14 items-center justify-center rounded-full bg-neutral-100">
          <svg className="h-6 w-6 text-neutral-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 19.128a9.38 9.38 0 002.625.372 9.337 9.337 0 004.121-.952 4.125 4.125 0 00-7.533-2.493M15 19.128v-.003c0-1.113-.285-2.16-.786-3.07M15 19.128v.106A12.318 12.318 0 018.624 21c-2.331 0-4.512-.645-6.374-1.766l-.001-.109a6.375 6.375 0 0111.964-3.07M12 6.375a3.375 3.375 0 11-6.75 0 3.375 3.375 0 016.75 0zm8.25 2.25a2.625 2.625 0 11-5.25 0 2.625 2.625 0 015.25 0z" />
          </svg>
        </div>
        <p className="mt-4 font-medium text-neutral-700">Nessun iscritto trovato</p>
        <p className="mt-1 text-sm text-neutral-600">Gli iscritti dalla landing appariranno qui</p>
      </div>
    );
  }

  return (
    <div>
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="bg-neutral-50">
              <th className="px-5 py-3.5 text-left text-[11px] font-semibold uppercase tracking-wider text-neutral-700">Nome</th>
              <th className="px-5 py-3.5 text-left text-[11px] font-semibold uppercase tracking-wider text-neutral-700">Email</th>
              <th className="px-5 py-3.5 text-left text-[11px] font-semibold uppercase tracking-wider text-neutral-700">Tipo</th>
              <th className="px-5 py-3.5 text-left text-[11px] font-semibold uppercase tracking-wider text-neutral-700">Mail</th>
              <th className="px-5 py-3.5 text-left text-[11px] font-semibold uppercase tracking-wider text-neutral-700">Questionario</th>
              <th className="px-5 py-3.5 text-left text-[11px] font-semibold uppercase tracking-wider text-neutral-700">Registrato</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-neutral-100">
            {leads.map((lead) => (
              <tr key={lead._id} className="hover:bg-neutral-50/50 transition-colors">
                <td className="px-5 py-3.5">
                  <div className="flex items-center gap-3">
                    <Avatar fallback={lead.name} size="sm" />
                    <span className="font-medium text-neutral-900">{lead.name}</span>
                  </div>
                </td>
                <td className="px-5 py-3.5 text-sm text-neutral-600">{lead.email}</td>
                <td className="px-5 py-3.5">
                  <Badge variant={lead.type === 'USER' ? 'info' : 'secondary'}>
                    {lead.type === 'USER' ? 'Utente' : 'Professionista'}
                  </Badge>
                </td>
                <td className="px-5 py-3.5">
                  <WelcomeMailCell lead={lead} />
                </td>
                <td className="px-5 py-3.5">
                  {lead.questionnaireUrl ? (
                    <div className="flex flex-wrap items-center gap-2">
                      <Badge variant={questionnaireStatusLabels[lead.questionnaireStatus || '']?.variant || 'default'}>
                        {questionnaireStatusLabels[lead.questionnaireStatus || '']?.label || '—'}
                      </Badge>
                      <CopyLinkButton url={lead.questionnaireUrl} />
                      <MailStatusIcon lead={lead} />
                      <ResendQuestionnaireMailButton leadId={lead._id} />
                    </div>
                  ) : lead.type === 'PROFESSIONAL' ? (
                    <GenerateButton leadId={lead._id} />
                  ) : (
                    <span className="text-sm text-neutral-600">—</span>
                  )}
                </td>
                <td className="px-5 py-3.5 text-sm text-neutral-600">
                  {new Date(lead.createdAt).toLocaleDateString('it-IT', {
                    day: 'numeric',
                    month: 'short',
                    year: 'numeric',
                  })}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {pagination && pagination.totalPages > 1 && (
        <div className="flex items-center justify-between border-t border-neutral-100 px-5 py-3.5">
          <p className="text-sm text-neutral-600">
            Pagina {pagination.page} di {pagination.totalPages} ({pagination.total} risultati)
          </p>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => onPageChange(page - 1)}
              disabled={!pagination.hasPrevPage}
            >
              Precedente
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => onPageChange(page + 1)}
              disabled={!pagination.hasNextPage}
            >
              Successiva
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
